import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Calendar, User, Filter, Search } from "lucide-react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Predefined sport categories (same as admin)
const SPORT_CATEGORIES = [
  { id: "all", label: "All Sports", icon: "🏆" },
  { id: "mens_college_basketball", label: "Mens College Basketball", icon: "🏀", short: "MCB" },
  { id: "womens_college_basketball", label: "Womens College Basketball", icon: "🏀", short: "WCB" },
  { id: "boys_hs_basketball", label: "Boys High School Basketball", icon: "🏀", short: "BHS" },
  { id: "girls_hs_basketball", label: "Girls High School Basketball", icon: "🏀", short: "GHS" },
  { id: "college_football", label: "College Football", icon: "🏈", short: "CFB" },
  { id: "hs_football", label: "High School Football", icon: "🏈", short: "HSF" },
];

export const RecordBookPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Get current sport info
  const currentSport = SPORT_CATEGORIES.find(s => s.id === activeTab);

  useEffect(() => {
    fetchRecords();
  }, [activeTab]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      let params = "";
      if (activeTab !== "all") {
        const sportLabel = SPORT_CATEGORIES.find(s => s.id === activeTab)?.label;
        if (sportLabel) {
          params = `?sport=${encodeURIComponent(sportLabel)}`;
        }
      }
      const response = await axios.get(`${API}/records${params}`);
      setRecords(response.data || []);
    } catch (error) {
      console.error("Error fetching records:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search
  const filteredRecords = records.filter(record => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      record.player?.toLowerCase().includes(query) ||
      record.sport?.toLowerCase().includes(query) ||
      record.total?.toLowerCase().includes(query) ||
      record.category?.toLowerCase().includes(query)
    );
  });

  // Group by sport (for "All Sports" view)
  const groupedRecords = filteredRecords.reduce((acc, record) => {
    const sport = record.sport || "Other";
    if (!acc[sport]) acc[sport] = [];
    acc[sport].push(record);
    return acc;
  }, {});

  // Sort sports in the order of SPORT_CATEGORIES
  const sortedGroupKeys = Object.keys(groupedRecords).sort((a, b) => {
    const indexA = SPORT_CATEGORIES.findIndex(s => s.label === a);
    const indexB = SPORT_CATEGORIES.findIndex(s => s.label === b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });

  return (
    <div className="min-h-screen bg-black" data-testid="record-book-page">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-16 md:py-24 bg-gradient-to-b from-zinc-900 to-black border-b border-zinc-800">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-orange-500/10 via-transparent to-transparent"></div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl uppercase text-white mb-4">
              Record <span className="text-orange-500">Book</span>
            </h1>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              Celebrating the greatest performances in ASN broadcast history
            </p>
          </motion.div>
        </div>
      </section>

      {/* Sport Tabs */}
      <section className="py-4 bg-zinc-900 border-b border-zinc-800 sticky top-0 z-20">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-2 justify-center" data-testid="sport-tabs">
            {SPORT_CATEGORIES.map((sport) => (
              <button
                key={sport.id}
                onClick={() => setActiveTab(sport.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-xs md:text-sm font-bold uppercase transition-all ${
                  activeTab === sport.id
                    ? 'bg-orange-500 text-black'
                    : 'bg-zinc-800 text-zinc-400 hover:text-white hover:bg-zinc-700'
                }`}
                data-testid={`tab-${sport.id}`}
              >
                <span>{sport.icon}</span>
                <span className="hidden md:inline">{sport.id === "all" ? sport.label : sport.label}</span>
                <span className="md:hidden">{sport.short || sport.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="py-4 bg-black border-b border-zinc-800">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-col md:flex-row items-center gap-4 justify-between">
            {/* Current Sport Info */}
            <div className="flex items-center gap-3">
              <span className="text-2xl">{currentSport?.icon}</span>
              <div>
                <h2 className="text-lg font-heading uppercase text-white">{currentSport?.label}</h2>
                <p className="text-xs text-zinc-500">{filteredRecords.length} records</p>
              </div>
            </div>
            
            {/* Search */}
            <div className="relative w-full md:w-auto md:min-w-[300px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search players, records..."
                className="w-full bg-zinc-900 border border-zinc-700 text-white pl-10 pr-4 py-2.5 text-sm focus:border-orange-500 transition-colors"
                data-testid="record-search"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Records Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {loading ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 border-2 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-zinc-500">Loading records...</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="text-center py-16">
              <span className="text-6xl mb-4 block">{currentSport?.icon}</span>
              <p className="text-zinc-400 text-lg">No {currentSport?.label} records found</p>
              {searchQuery && (
                <p className="text-zinc-600 mt-2">Try adjusting your search</p>
              )}
            </div>
          ) : (
            <div className="space-y-12">
              {sortedGroupKeys.map((sport, sportIndex) => {
                const sportRecords = groupedRecords[sport];
                const sportInfo = SPORT_CATEGORIES.find(s => s.label === sport);
                
                return (
                  <motion.div
                    key={sport}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: sportIndex * 0.1 }}
                  >
                    {/* Sport Header - Only show in "All Sports" view */}
                    {activeTab === "all" && (
                      <div className="flex items-center gap-4 mb-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-orange-500 flex items-center justify-center">
                            <span className="text-lg">{sportInfo?.icon || "🏆"}</span>
                          </div>
                          <h2 className="font-heading text-xl md:text-2xl uppercase text-white">
                            {sport}
                          </h2>
                        </div>
                        <div className="flex-1 h-px bg-gradient-to-r from-zinc-800 to-transparent"></div>
                        <span className="text-zinc-500 text-sm">{sportRecords.length} records</span>
                      </div>
                    )}

                    {/* Records Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {sportRecords.map((record, idx) => (
                        <motion.div
                          key={record.id}
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: idx * 0.03 }}
                          className="bg-zinc-900 border border-zinc-800 hover:border-orange-500/50 transition-colors p-6 group"
                          data-testid={`record-card-${record.id}`}
                        >
                          {/* Category Badge */}
                          {record.category && (
                            <span className="inline-block px-2 py-1 bg-orange-500/10 text-orange-500 text-[10px] font-bold uppercase tracking-wider mb-3">
                              {record.category}
                            </span>
                          )}
                          
                          {/* Player Name */}
                          <div className="flex items-center gap-2 mb-3">
                            <User className="w-4 h-4 text-zinc-500" />
                            <h3 className="text-lg font-bold text-white group-hover:text-orange-500 transition-colors">
                              {record.player}
                            </h3>
                          </div>

                          {/* Record Value */}
                          <div className="text-3xl font-heading text-orange-500 mb-3">
                            {record.total}
                          </div>

                          {/* Date */}
                          {record.date && (
                            <div className="flex items-center gap-2 text-sm text-zinc-500">
                              <Calendar className="w-4 h-4" />
                              {record.date}
                            </div>
                          )}

                          {/* Notes */}
                          {record.notes && (
                            <p className="text-xs text-zinc-600 mt-3 italic">
                              {record.notes}
                            </p>
                          )}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default RecordBookPage;
